// Utilities at move-in (Sahil, 2026-10-05).
//
// When a property goes from vacant to occupied, every utility the owner pays
// needs an explicit answer: the tenant takes it over, or the owner keeps
// paying. Nothing is assumed and the move-in cannot save until each is
// answered. "Tenant takes over" flips the line owner->tenant; the database
// then keeps the owner's account NUMBER and login on the owner's final-bill
// line (the tenant never takes over the owner's account -- they open their
// own) and starts the tenant line with neither.
//
// Pure rules over the wizard's form rows and the stored rows, so they can be
// tested without a browser or a database.

const DECISIONS = new Set(["tenant", "owner"]);

// The wizard rows that need an answer: a real provider, owner-paid, not the
// owner's final bill. A condo-fee utility has no bill of its own and a tenant
// line already is the tenant's.
export function moveInUtilityRows(utilities) {
  return (utilities || []).filter(u =>
    u && String(u.provider || "").trim()
    && (u.responsibility || "owner_pays") === "owner_pays"
    && !u.is_final_bill);
}

// Providers still without an answer (the message names them).
export function moveInDecisionsPending(utilities) {
  return moveInUtilityRows(utilities)
    .filter(u => !DECISIONS.has(u.movein))
    .map(u => String(u.provider).trim());
}

// What the save sends: a row the tenant takes over becomes tenant-paid. The
// flip itself (final-bill line, number, login) is the database's.
export function applyMoveInDecisions(utilities) {
  return (utilities || []).map(u =>
    u && u.movein === "tenant" && (u.responsibility || "owner_pays") === "owner_pays" && !u.is_final_bill
      ? { ...u, responsibility: "tenant_pays" }
      : u);
}

// The stored rows (ids) a decision was made about -- both answers -- so the
// save can stamp them decided (movein_decided_at). An unsaved row has no id.
export function moveInDecidedIds(utilities) {
  return moveInUtilityRows(utilities).filter(u => DECISIONS.has(u.movein) && u.id).map(u => u.id);
}

// Is this wizard session a move-in? Occupied now, and either opened to add a
// tenant, opened from the "Utilities at move-in" task, or loaded a property
// that was not occupied when the wizard opened.
export function isMoveIn({ status, wizardData, loadedStatus }) {
  if (status !== "occupied") return false;
  if (wizardData && (wizardData.addingTenant || wizardData.moveInUtilities)) return true;
  return !!loadedStatus && loadedStatus !== "occupied";
}

// TASKS & APPROVALS safety net. Every path that occupies a property runs
// through the wizard except a lease created on the Leases page, a tenant
// restored, or an import. For those, an occupied property whose owner-paid
// utility has no decision since the tenant moved in gets one to-do, which
// opens the wizard's utilities step in move-in mode.
//   utilRows:    live utilities rows { id, property, provider, responsibility, is_final_bill, movein_decided_at }
//   properties:  { id, address, status }
//   tenants:     live tenancies { property, lease_start, move_in }
export function buildMoveInUtilityTasks(utilRows, properties, tenants) {
  const propsByAddr = new Map((properties || []).filter(p => p && p.address).map(p => [p.address, p]));
  const movedIn = new Map();
  for (const t of tenants || []) {
    if (!t || !t.property) continue;
    const d = String(t.move_in || t.lease_start || "").slice(0, 10);
    // The newest tenancy at the address decides "since the tenant moved in".
    if (d && (!movedIn.has(t.property) || d > movedIn.get(t.property))) movedIn.set(t.property, d);
  }
  const byProp = new Map();
  for (const u of utilRows || []) {
    if (!u || !u.property || u.archived_at) continue;
    if (u.is_final_bill) continue;
    if ((u.responsibility || "owner") !== "owner") continue;
    const p = propsByAddr.get(u.property);
    if (!p || p.status !== "occupied") continue;
    const since = movedIn.get(u.property) || "";
    const decided = String(u.movein_decided_at || "").slice(0, 10);
    if (decided && decided >= since) continue;
    const list = byProp.get(u.property) || [];
    list.push(String(u.provider || "").trim() || "utility");
    byProp.set(u.property, list);
  }
  const tasks = [];
  for (const [address, providers] of byProp) {
    const p = propsByAddr.get(address);
    tasks.push({
      icon: "🔌",
      title: "Utilities at move-in: who pays " + providers.join(", ") + "?",
      subtitle: address,
      address,
      propertyId: p.id,
      link: "properties",
      priority: "high",
      _kind: "movein_utilities",
      providers,
    });
  }
  return tasks.sort((a, b) => a.address.localeCompare(b.address));
}
